import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  NavigationBehaviorOptions,
  NavigationEnd,
  NavigationStart,
  Router,
  UrlTree,
} from '@angular/router';

/** Entrada del historial del tab, tal como la dejó la app. */
interface HistoryEntry {
  /** URL de la barra (con query params y fragmento). */
  url: string;
  /** La pantalla es parte de un flujo (layout de pasos, el que cierra con la X). */
  isFlow: boolean;
}

/** Navegación que espera a que el navegador termine de retroceder (ver `traverse`). */
interface PendingNavigation {
  url: string;
  extras: NavigationBehaviorOptions;
}

/**
 * Clave con la que el router guarda en `history.state` la posición de cada entrada. Solo
 * existe con `canceledNavigationResolution: 'computed'` (main.ts); sin ella el servicio cae
 * a navegar como antes.
 */
const ROUTER_PAGE_ID_KEY = 'ɵrouterPageId';
/** Layout de los flujos: depósito, rescate, crear o editar una meta… */
const FLOW_LAYOUT = 'bpstepsheader';
const HOME_URL = '/dashboard';

/**
 * Mantiene el historial del tab coherente al salir de un flujo.
 *
 * Problema que resuelve: un flujo (crear meta → depósito, editar la meta…) deja sus pantallas
 * en el historial. Al terminarlo o cerrarlo con la X, "atrás" volvía a meter al usuario en
 * él. Y no hay una sola flecha que corregir: en la app, el atrás de Android (botón o gesto)
 * y el gesto de iOS retroceden en el historial del WebView sin pasar por el portal.
 *
 * Por eso la salida reescribe el historial: retrocede hasta la pantalla desde donde se abrió
 * el flujo y recién ahí muestra el destino. El flujo queda adelante, fuera del camino de
 * "atrás".
 *
 * Para saber dónde está cada pantalla, el router numera las entradas (`ɵrouterPageId`) y este
 * servicio anota la URL de cada una al terminar cada navegación. Las anotaciones viven en
 * memoria: tras recargar la página faltan y la salida cae a navegar sin reescribir.
 */
@Injectable({
  providedIn: 'root',
})
export class BpHistoryService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly entries = new Map<number, HistoryEntry>();
  /** Retroceso en curso; `navigation` es lo que se muestra al llegar (ver `traverse`). */
  private traversal: { navigation?: PendingNavigation } | null = null;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.onNavigationStart(event);
      } else if (event instanceof NavigationEnd) {
        this.recordCurrentEntry();
      }
    });
  }

  /**
   * Cierra el flujo (X, cancelar): vuelve a la pantalla desde donde se abrió si es la misma
   * de `fallback`, tal como estaba (por ejemplo, la pestaña de ajustes de la meta). Si se
   * abrió desde otra, muestra `fallback` en lugar del flujo.
   */
  closeFlow(fallback: UrlTree): void {
    this.leaveFlow(this.router.serializeUrl(fallback), true);
  }

  /**
   * Termina el flujo mostrando `target` (por ejemplo, la meta con las instrucciones del
   * depósito). Si el flujo se abrió desde esa misma pantalla, `target` la reemplaza para que
   * no quede repetida.
   */
  finishFlow(target: UrlTree): void {
    this.leaveFlow(this.router.serializeUrl(target), false);
  }

  /**
   * Vuelve al inicio retrocediendo hasta su entrada más cercana, para que el atrás del
   * sistema siga el mismo camino que la flecha. Si el inicio no está en el historial, navega.
   */
  backToHome(): void {
    if (this.traversal) return;
    const delta = this.deltaToNearest(HOME_URL);
    if (delta !== undefined) {
      this.traverse(delta);
      return;
    }
    void this.router.navigateByUrl(HOME_URL);
  }

  /**
   * Sube a la pantalla padre (`path`, sin query params ni fragmento) retrocediendo hasta su
   * entrada más cercana, como `backToHome`: lo recorrido entre medio queda adelante, fuera del
   * camino de "atrás". Si el padre no está en el historial (se entró directo por un link),
   * reemplaza la pantalla actual por él.
   */
  backTo(path: string): void {
    if (this.traversal) return;
    const delta = this.deltaToNearest(path);
    if (delta !== undefined) {
      this.traverse(delta);
      return;
    }
    void this.router.navigateByUrl(path, { replaceUrl: true });
  }

  /** Cuántas entradas hay que retroceder hasta la más cercana con esa ruta, si está anotada. */
  private deltaToNearest(path: string): number | undefined {
    const currentId = this.currentPageId();
    if (currentId === undefined) return undefined;
    for (let id = currentId - 1; id >= 0; id--) {
      const url = this.entries.get(id)?.url;
      if (url !== undefined && urlPath(url) === path) {
        return id - currentId;
      }
    }
    return undefined;
  }

  private leaveFlow(url: string, returnToOrigin: boolean): void {
    // Doble toque: ya hay un retroceso en curso; repetirlo retrocedería de más.
    if (this.traversal) return;
    const currentId = this.currentPageId();
    // Fuera de un flujo (o sin anotar, p. ej. tras recargar) se navega como siempre. Cubre
    // también una salida repetida: si ya se salió, no se vuelve a reescribir el historial.
    if (currentId === undefined || !this.entries.get(currentId)?.isFlow) {
      void this.router.navigateByUrl(url);
      return;
    }

    // Origen: la primera pantalla antes del flujo. Una entrada sin anotar (p. ej. tras
    // recargar) se toma como origen, porque no se sabe si es parte del flujo.
    let originId = currentId - 1;
    while (originId >= 0 && this.entries.get(originId)?.isFlow) {
      originId--;
    }

    if (originId < 0) {
      // Se entró directo al flujo (link, notificación): el destino ocupa su primera entrada.
      this.traverse(-currentId, { url, extras: { replaceUrl: true } });
      return;
    }

    const originUrl = this.entries.get(originId)?.url;
    const isSameScreen = originUrl !== undefined && urlPath(originUrl) === urlPath(url);
    if (isSameScreen && (returnToOrigin || originUrl === url)) {
      this.traverse(originId - currentId);
    } else if (isSameScreen) {
      this.traverse(originId - currentId, { url, extras: { replaceUrl: true } });
    } else {
      this.traverse(originId - currentId, { url, extras: {} });
    }
  }

  /**
   * Retrocede `delta` entradas y, si hay `navigation`, la muestra al llegar.
   *
   * La navegación se lanza en el `NavigationStart` del retroceso: el router descarta la del
   * retroceso al ver una más nueva, así que la pantalla intermedia nunca se activa (ni
   * parpadea ni pide sus datos). Como el navegador ya está en la entrada de destino, una
   * navegación normal agrega encima y borra lo que quedaba adelante (el flujo).
   */
  private traverse(delta: number, navigation?: PendingNavigation): void {
    if (delta === 0) {
      if (navigation) void this.router.navigateByUrl(navigation.url, navigation.extras);
      return;
    }
    this.traversal = { navigation };
    this.location.historyGo(delta);
  }

  private onNavigationStart(event: NavigationStart): void {
    const traversal = this.traversal;
    if (!traversal) return;
    this.traversal = null;
    // Cualquier otra navegación (no la del retroceso) deja sin efecto la pendiente.
    if (event.navigationTrigger !== 'popstate' || !traversal.navigation) return;
    const { url, extras } = traversal.navigation;
    void this.router.navigateByUrl(url, extras);
  }

  private recordCurrentEntry(): void {
    const pageId = this.currentPageId();
    if (pageId === undefined) return;
    this.entries.set(pageId, {
      url: this.location.path(true),
      isFlow: this.currentLayout() === FLOW_LAYOUT,
    });
  }

  private currentPageId(): number | undefined {
    const state = this.location.getState() as Record<string, unknown> | null;
    const pageId = state?.[ROUTER_PAGE_ID_KEY];
    return typeof pageId === 'number' ? pageId : undefined;
  }

  /** Layout de la ruta activa, con el mismo criterio que `LayoutComponent`. */
  private currentLayout(): unknown {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let layout: unknown;
    while (route) {
      layout = route.routeConfig?.data?.['layout'] ?? layout;
      route = route.firstChild;
    }
    return layout;
  }
}

/** Ruta de una URL, sin query params ni fragmento. */
function urlPath(url: string): string {
  return url.split(/[?#]/)[0];
}
